




























































import { defineComponent, onMounted, onUnmounted, PropType, ref, toRef, watch } from "@vue/composition-api";
import { fabric } from "fabric";
import { Canvas, IEvent, Object as ObjectCanvas } from "fabric/fabric-impl";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import BaseIconButton from "@/components/BaseIconButton";
import ColorPicker from "./components/ColorPicker";
import FontAlign from "./components/FontAlign";
import FontBackground from "./components/FontBackground";
import FontRange from "./components/FontRange";
import FontType from "./components/FontType";
import useObjects from "./hooks/useObjects";
import { FillBackgroundType, FontsType, ObjectType, TextAlign, textFonts } from "../../../config";
import { Overlay } from "../../../types";

export default defineComponent({
  name: "StoryOverlayController",
  components: {
    FontBackground,
    FontType,
    FontAlign,
    FontRange,
    BaseIconButton,
    BaseButton,
    BaseConfirm,
    ColorPicker,
  },
  props: {
    overlay: { type: Object as PropType<Overlay> },
    overlayRef: { type: HTMLCanvasElement },
    needRerender: { type: Boolean },
  },
  setup(props, { emit }) {
    /** Canvas */
    const canvasInstance = ref<Canvas | undefined>(props.overlay?.instance);
    const canvasInstanceConfig = ref(props.overlay?.config);

    /** Show flags */
    const showColorPicker = ref(false);
    const showDeleteConfirm = ref(false);

    const resetControllersToDefaultState = () => {
      showColorPicker.value = false;
      showDeleteConfirm.value = false;
    };

    const {
      currentIdGroup,
      currentTextAlign,
      currentTextSize,
      currentFont,
      currentColorIndex,
      currentFillType,
      cursorIsChanged,
      editableTextObject,
      editableBackgroundObjects,
      allGroups,
      showDeleteObjectIcon,
      addListeners,
      removeListeners,
      createTextObject,
      deleteObjects,
      changeBackground,
      paintingOverObjects,
      setTextPositionToCenterOfCanvas,
      handleExitEditingText,
    } = useObjects(canvasInstance, resetControllersToDefaultState);

    const customControlsDrawing = (objectCanvas: ObjectCanvas) => {
      const renderIcon = (icon: string) => {
        return function renderIcon(ctx: CanvasRenderingContext2D, left: number, top: number) {
          let iconEl = document.createElement("img");
          iconEl.src = icon;
          let size = 36;
          ctx.save();
          ctx.translate(left, top);
          ctx.drawImage(iconEl, -size / 2, -size / 2, size, size);
          ctx.restore();
        };
      };

      objectCanvas.controls.br = new fabric.Control({
        x: 0.5,
        y: 0.5,
        render: renderIcon("/img/icons/sci-color-resize-block.svg"),
        actionName: objectCanvas.controls.br.actionName,
        actionHandler: objectCanvas.controls.br.actionHandler,
        cursorStyleHandler: objectCanvas.controls.br.cursorStyleHandler,
      });

      objectCanvas.controls.tl = new fabric.Control({
        x: -0.5,
        y: -0.5,
        render: renderIcon("/img/icons/sci-color-resize-block.svg"),
        actionName: objectCanvas.controls.tl.actionName,
        actionHandler: objectCanvas.controls.tl.actionHandler,
        cursorStyleHandler: objectCanvas.controls.tl.cursorStyleHandler,
      });

      objectCanvas.controls.mtr = new fabric.Control({
        x: 0,
        y: -0.5,
        render: renderIcon("/img/icons/sci-color-rotate.svg"),
        actionName: objectCanvas.controls.mtr.actionName,
        actionHandler: objectCanvas.controls.mtr.actionHandler,
        cursorStyleHandler: objectCanvas.controls.mtr.cursorStyleHandler,
      });

      canvasInstance.value?.renderAll();
    };

    const onLeaveCanvas = () => {
      const textLength = editableTextObject.value?.text?.length ?? 0;
      editableTextObject.value?.setSelectionStart(textLength);
      editableTextObject.value?.setSelectionEnd(textLength);
      editableTextObject.value?.hiddenTextarea?.focus();
    };

    const checkCanvasOnEditing = () => {
      const objects = canvasInstance.value?.getObjects();
      return objects?.find((object: any) => object.type === ObjectType.TEXT_BLOCK && object.isEditing);
    };

    const handleClickOnCanvas = (e: IEvent) => {
      if (!canvasInstance.value) return;

      const activeObject = canvasInstance.value.getActiveObject();
      const isEditing = checkCanvasOnEditing();

      if (!activeObject && !isEditing) {
        createTextObject();
        setTextPositionToCenterOfCanvas(e);

        if (editableTextObject.value) {
          customControlsDrawing(editableTextObject.value);
          canvasInstance.value.add(editableTextObject.value);
        }
        /** cursorIsChanged.value = true on mousedown in useObject */
      } else if (!cursorIsChanged.value) {
        editableTextObject.value?.exitEditing();
        editableTextObject.value = undefined;
      }

      showDeleteObjectIcon.value = !!activeObject;
      cursorIsChanged.value = false;
      editableTextObject.value?.enterEditing();
    };

    const handleToggleMoreSettings = () => {
      onLeaveCanvas();
      showColorPicker.value = !showColorPicker.value;
    };

    const handleChangeTextAlign = (textAlign: TextAlign) => {
      onLeaveCanvas();

      if (!canvasInstance.value || !editableTextObject.value || currentTextAlign.value === textAlign) return;

      editableTextObject.value.set("textAlign", textAlign);
      currentTextAlign.value = textAlign;

      editableBackgroundObjects.value.forEach((item: any) => {
        if (currentFont.value === FontsType.INTER) {
          item._objects.forEach((i: fabric.Textbox) => i.set("textAlign", textAlign));
        } else {
          const leftPosition: Record<TextAlign, number> = {
            [TextAlign.LEFT]: 5,
            [TextAlign.CENTER]: (canvasInstance.value?.width ?? 0) / 2 - item.width / 2,
            [TextAlign.RIGHT]: (canvasInstance.value?.width ?? 0) - item.width - 5,
          };
          item.set("left", leftPosition[textAlign]);
        }
      });

      canvasInstance.value.renderAll();
    };

    const handleChangeColor = (colorIndex: number) => {
      onLeaveCanvas();

      if (currentColorIndex.value === colorIndex) return;

      currentColorIndex.value = colorIndex;
      paintingOverObjects();
    };

    const handleChangeFillType = (fillType: FillBackgroundType) => {
      onLeaveCanvas();

      if (currentFillType.value === fillType) return;

      currentFillType.value = fillType;
      changeBackground();
    };

    const handleChangeFont = (textFont: FontsType) => {
      onLeaveCanvas();

      if (!editableTextObject.value || currentFont.value === textFont) return;

      editableTextObject.value.set("fontFamily", textFonts[textFont].name);
      currentFont.value = textFont;

      const fontWeights: Record<FontsType, number> = {
        [FontsType.COURIER_NEW]: 700,
        [FontsType.TINOS]: 700,
        [FontsType.VOLLKORN]: 700,
        [FontsType.INTER]: 900,
        [FontsType.CAVEAT_BRUSH]: 400,
      };
      editableTextObject.value.set("fontWeight", fontWeights[textFont]);
      changeBackground();
    };

    const handleChangeTextSize = (textSize: number) => {
      onLeaveCanvas();

      if (!canvasInstance.value || currentTextSize.value === textSize) return;

      editableTextObject.value?.set("fontSize", textSize);
      currentTextSize.value = textSize;
      changeBackground();
    };

    const handleDeleteGroup = () => {
      resetControllersToDefaultState();
      deleteObjects();
    };

    const handleSave = () => {
      if (!canvasInstance.value) return;
      handleExitEditingText();

      allGroups.value?.forEach(item => {
        removeListeners(item.group);
      });

      if (editableBackgroundObjects.value.length && editableTextObject.value && editableTextObject.value?.isEditing) {
        editableTextObject.value?.exitEditing();
      }

      canvasInstance.value.discardActiveObject();
      canvasInstance.value.renderAll();
      canvasInstanceConfig.value = canvasInstance.value.toJSON();
      setTimeout(() => {
        emit(
          "saveOverlay",
          canvasInstanceConfig.value.objects.length > 0
            ? canvasInstance.value?.toDataURL({ format: "png", multiplier: 1080 / (props.overlay?.width ?? 1080) })
            : "",
          allGroups.value,
          canvasInstanceConfig.value,
          canvasInstance.value
        );
      }, 100);
    };

    const resizeOverlay = () => {
      if (!props.overlayRef) return;

      props.overlayRef.width = props.overlay?.width ?? 0;
      props.overlayRef.height = props.overlay?.height ?? 0;

      const scaleMultiplier = (props.overlay?.width ?? 1) / (canvasInstance.value?.width ?? 1);
      const objects = canvasInstance.value?.getObjects();
      objects?.forEach(item => {
        if (!item) return;
        item.scaleX = (item.scaleX ?? 1) * scaleMultiplier;
        item.scaleY = (item.scaleY ?? 1) * scaleMultiplier;
        item.left = (item.left ?? 1) * scaleMultiplier;
        item.top = (item.top ?? 1) * scaleMultiplier;
        item.setCoords();
      });

      canvasInstance.value?.setWidth(canvasInstance.value?.getWidth() * scaleMultiplier);
      canvasInstance.value?.setHeight(canvasInstance.value?.getHeight() * scaleMultiplier);
      canvasInstance.value?.renderAll();
      canvasInstance.value?.calcOffset();
    };

    const setControlsView = () => {
      const PALETTE_BG = "#ffffff";
      const PALETTE_GRAY_LIGHTEN_7 = "#eaeaeb";
      const prototypeObjectClass = fabric.Object.prototype;

      prototypeObjectClass.transparentCorners = false;
      prototypeObjectClass.cornerColor = PALETTE_BG;
      prototypeObjectClass.cornerStyle = "circle";
      prototypeObjectClass.stroke = PALETTE_GRAY_LIGHTEN_7;
      prototypeObjectClass.borderColor = PALETTE_GRAY_LIGHTEN_7;
      prototypeObjectClass.borderScaleFactor = 1;
      prototypeObjectClass.borderOpacityWhenMoving = 0.2;
      prototypeObjectClass.padding = 16;
      prototypeObjectClass.hasBorders = true;

      prototypeObjectClass.setControlsVisibility({
        bl: false,
        br: true,
        mb: false,
        ml: false,
        mr: false,
        mt: false,
        tl: true,
        tr: false,
        mtr: true,
      });
    };

    const initialCanvasInstance = () => {
      resizeOverlay();

      allGroups.value = props.overlay?.groups ?? [];
      allGroups.value?.forEach(item => {
        addListeners(item.group);
      });
      currentIdGroup.value =
        allGroups.value?.length > 0 ? (Number(allGroups.value[allGroups.value?.length - 1].id) ?? 0) + 1 : 0;

      if (props.needRerender) {
        canvasInstance.value = new fabric.Canvas(props.overlayRef ?? null, { selection: false });
        editableBackgroundObjects.value = [];
        allGroups.value?.forEach(item => {
          canvasInstance.value?.add(item.group);
        });
      }

      setControlsView();
      customControlsDrawing(fabric.Object.prototype);

      fabric.util.requestAnimFrame(function render() {
        canvasInstance.value?.renderAll();
        fabric.util.requestAnimFrame(render);
      });

      canvasInstance.value?.on("mouse:up", handleClickOnCanvas);
    };

    watch(
      toRef(props, "overlayRef"),
      () => {
        if (props.overlayRef) initialCanvasInstance();
      },
      { immediate: true }
    );

    onMounted(() => {
      window.addEventListener("resize", resizeOverlay);
    });

    onUnmounted(() => {
      canvasInstance.value?.off("mouse:up");

      editableTextObject.value?.exitEditing();
      editableTextObject.value = undefined;
      window.removeEventListener("resize", resizeOverlay);
    });

    return {
      editableTextObject,
      currentFont,
      currentFillType,
      showDeleteObjectIcon,
      canvasInstance,
      currentTextSize,
      currentTextAlign,
      showColorPicker,
      showDeleteConfirm,
      canvasInstanceConfig,
      currentColorIndex,
      allGroups,
      onLeaveCanvas,
      handleSave,
      handleToggleMoreSettings,
      handleChangeTextAlign,
      handleChangeFillType,
      handleChangeColor,
      handleChangeFont,
      handleChangeTextSize,
      handleDeleteGroup,
    };
  },
});
